/**
 * Filter Unlock Banner
 * 
 * Shows progress toward unlocking filters.
 * Displays when user has < required ratings.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Unlock, Star, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

// Unlock thresholds
const UNLOCK_THRESHOLDS = {
    basicFilters: 10,
    advancedFilters: 25,
    recommendations: 50,
    compatibility: 100
};

function FilterUnlockBanner({
    totalRatings = 0,
    targetFeature = 'basicFilters', // Which unlock to target
    compact = false,
    onDismiss
}) {
    const threshold = UNLOCK_THRESHOLDS[targetFeature];
    const isUnlocked = totalRatings >= threshold;
    const remaining = Math.max(0, threshold - totalRatings);
    const progress = Math.min(100, (totalRatings / threshold) * 100);

    const featureLabels = {
        basicFilters: 'Basic Filters',
        advancedFilters: 'Advanced Filters',
        recommendations: 'Personalized Recommendations',
        compatibility: 'Partner Compatibility'
    };

    const featureDescriptions = {
        basicFilters: 'Filter by category and intensity',
        advancedFilters: 'Filter by experience and context',
        recommendations: 'Get personalized "For You" suggestions',
        compatibility: 'See compatibility scores with partners'
    };

    // Already unlocked - show celebration briefly or nothing
    if (isUnlocked) {
        if (onDismiss) {
            return (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border border-emerald-500/30 p-3 flex items-center gap-3"
                >
                    <Unlock className="w-5 h-5 text-emerald-400" />
                    <span className="text-sm text-cream flex-1">
                        <strong className="text-emerald-400">{featureLabels[targetFeature]}</strong> unlocked!
                    </span>
                    <button
                        onClick={onDismiss}
                        className="text-xs text-cream/50 hover:text-cream"
                    >
                        Dismiss
                    </button>
                </motion.div>
            );
        }
        return null;
    }

    if (compact) {
        return (
            <div className="flex items-center gap-2 text-xs text-dark-400">
                <Lock size={12} />
                <span>Rate {remaining} more to unlock filters</span>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card border border-wine-700/30 p-4"
        >
            <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-wine-900/30 flex items-center justify-center flex-shrink-0">
                    <Lock className="w-6 h-6 text-wine-400" />
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-cream">{featureLabels[targetFeature]}</h4>
                        <span className="text-xs px-2 py-0.5 bg-wine-900/30 text-wine-300">
                            {remaining} to go
                        </span>
                    </div>

                    <p className="text-sm text-dark-300 mb-3">
                        {featureDescriptions[targetFeature]}
                    </p>

                    {/* Progress bar */}
                    <div className="h-2 bg-dark-800 overflow-hidden mb-2">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 0.5, ease: 'easeOut' }}
                            className="h-full bg-gradient-to-r from-wine-600 to-wine-400"
                        />
                    </div>

                    <div className="flex items-center justify-between text-xs">
                        <span className="text-dark-400">
                            <Star className="w-3 h-3 inline mr-1" />
                            {totalRatings} rated
                        </span>
                        <span className="text-wine-400">
                            {threshold} needed
                        </span>
                    </div>
                </div>
            </div>

            {/* CTA */}
            <Link
                to="/"
                className="mt-4 flex items-center justify-center gap-2 w-full py-2 bg-wine-900/30 hover:bg-wine-800/40 text-wine-300 text-sm transition-colors"
            >
                <Sparkles size={14} />
                Rate more kinks
                <ArrowRight size={14} />
            </Link>
        </motion.div>
    );
}

export default FilterUnlockBanner;
